import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, AlertTriangle, ArrowRight } from "lucide-react";
import { calculateDaysRemaining, isTrialExpired } from "../../api/trialAPI";

interface TrialBannerProps {
  trialExpiresAt: string | null;
  tenantSlug: string;
  membershipStatus?: string;
  onClose?: () => void;
}

/**
 * TrialBanner - Displays trial status for members
 *
 * Shows different states:
 * - Active trial: Yellow banner with days remaining
 * - Trial expired: Red banner prompting conversion
 */
const TrialBanner: React.FC<TrialBannerProps> = ({
  trialExpiresAt,
  tenantSlug,
  membershipStatus,
  onClose,
}) => {
  const navigate = useNavigate();

  const expired = membershipStatus === "trial_expired" || isTrialExpired(trialExpiresAt);
  const daysRemaining = calculateDaysRemaining(trialExpiresAt);

  const handleConvert = () => {
    navigate(`/${tenantSlug}/member/convert-trial`);
  };

  // Expired trial banner
  if (expired) {
    return (
      <div className="bg-red-600 text-white px-4 py-3 relative">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div>
              <span className="font-semibold">Your trial has expired.</span>
              <span className="ml-2">Complete your registration to continue using all features.</span>
            </div>
          </div>
          <button
            onClick={handleConvert}
            className="flex items-center gap-2 bg-white text-red-600 px-4 py-1.5 rounded-md font-semibold hover:bg-red-50 transition-colors text-sm"
          >
            Complete Registration
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-red-200 hover:text-white"
            aria-label="Close"
          >
            &times;
          </button>
        )}
      </div>
    );
  }

  // Active trial banner
  return (
    <div className="bg-yellow-500 text-yellow-900 px-4 py-3 relative">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="font-semibold">
              {daysRemaining} day{daysRemaining !== 1 ? "s" : ""} left in your trial.
            </span>
            <span className="ml-2 hidden sm:inline">
              Upgrade now to unlock full membership benefits!
            </span>
          </div>
        </div>
        <button
          onClick={handleConvert}
          className="flex items-center gap-2 bg-yellow-900 text-white px-4 py-1.5 rounded-md font-semibold hover:bg-yellow-800 transition-colors text-sm"
        >
          Become a Member
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-yellow-700 hover:text-yellow-900"
          aria-label="Close"
        >
          &times;
        </button>
      )}
    </div>
  );
};

export default TrialBanner;
